import { useSyncExternalStore } from 'react';
/**
 * A re-render trigger for hash changes the router does not deliver.
 *
 * This is ONLY a trigger — `useLocationHash()` remains the value. Subscribing to
 * it guarantees a render happens so that value gets re-read.
 *
 * Two things have to line up for a consumer to miss a hash change, and on
 * `/projects/:id/plan` they do:
 *
 * 1. `TaskPaneContextProvider` wraps its children in a `React.memo` keyed on
 *    element identity (`StableChildren`), so the task drawer never re-renders
 *    from its parent. Its own subscription is the only path it has.
 * 2. That subscription does not always fire. Reopening the SAME task after
 *    closing it left the drawer with zero renders while sibling consumers
 *    rendered with the new hash — so the hash was current and the notification
 *    simply never arrived. (`RouterProvider` also deliberately keeps
 *    `LocationContext` stale for hash-only changes — see `shouldSkip` in
 *    `@rocketlane/rocket-router` — so a consumer on that branch has nothing to
 *    fall back on either.)
 *
 * Both hash-reading surfaces subscribe: `useHash` (internal app) and
 * `useCustomerPortalHashMap` (customer portal, which addresses its task pane
 * through `hashMap.task_drawer`). They share one store because they are exposed
 * to one bug.
 *
 * Note the `history` wrapper is not optional: `pushState` fires NEITHER
 * `hashchange` NOR `popstate`, and every in-app navigation goes through it, so
 * plain listeners would only ever catch browser back/forward.
 *
 * The notify is deferred to a microtask on purpose. `router.navigate` calls
 * `pushState` and then emits synchronously; bumping inside the wrapper would
 * render before the router had published the new location, read the old value,
 * and leave us waiting on the same notification that just went missing.
 */ const hashListeners = new Set();
let hashRevision = 0;
let lastSeenHash = typeof window === 'undefined' ? '' : window.location.hash;
let isHistoryPatched = false;
const notifyIfHashChanged = ()=>{
    const next = window.location.hash;
    if (next === lastSeenHash) {
        return;
    }
    lastSeenHash = next;
    hashRevision += 1;
    hashListeners.forEach((listener)=>listener());
};
const scheduleHashCheck = ()=>{
    Promise.resolve().then(notifyIfHashChanged);
};
const patchHistoryOnce = ()=>{
    if (isHistoryPatched || typeof window === 'undefined') {
        return;
    }
    isHistoryPatched = true;
    [
        'pushState',
        'replaceState'
    ].forEach((method)=>{
        const original = window.history[method];
        window.history[method] = function patched(...args) {
            const result = original.apply(this, args);
            scheduleHashCheck();
            return result;
        };
    });
    window.addEventListener('popstate', scheduleHashCheck);
    window.addEventListener('hashchange', scheduleHashCheck);
};
const subscribeToHashRevision = (listener)=>{
    patchHistoryOnce();
    hashListeners.add(listener);
    return ()=>{
        hashListeners.delete(listener);
    };
};
const getHashRevision = ()=>hashRevision;
const getServerHashRevision = ()=>0;
/** Subscribes the calling component to hash changes. Returns nothing usable. */ export const useHashRevision = ()=>{
    useSyncExternalStore(subscribeToHashRevision, getHashRevision, getServerHashRevision);
};
